import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Ticket, Plus, Trash2, Loader2, Edit, DollarSign, Calendar, RefreshCw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

export const StudioClassPassesPage = () => {
  const { api } = useAuth();
  const [studio, setStudio] = useState(null);
  const [classPasses, setClassPasses] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', description: '', activity_type: '', passes_count: 10,
    price: 0, validity_days: 30, transferable: false,
    is_membership: false, membership_billing_cycle: 'monthly'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studioData, passesData] = await Promise.all([
        apiGet('/api/studios/me'),
        apiGet('/api/class-passes'),
      ]);
      setStudio(studioData);
      setActivityTypes(studioData.activity_types || []);
      const studioPasses = (passesData || []).filter(p => p.studio_id === studioData.id);
      setClassPasses(studioPasses);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    setFormLoading(true);
    try {
      if (isEditing && selectedPass) {
        await apiPut('/api/class-passes/{pass_id}', {
          params: { pass_id: selectedPass.id },
          body: { ...form, studio_id: studio.id },
        });
        toast.success('ClassPass updated!');
      } else {
        await apiPost('/api/class-passes', {
          body: { ...form, studio_id: studio.id },
        });
        toast.success('ClassPass created!');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      const detail = formatApiError(err, '');
      toast.error(detail || 'Failed to save ClassPass');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (passId) => {
    if (!window.confirm('Delete this ClassPass? Existing client purchases will not be affected.')) return;
    try {
      await apiDelete('/api/class-passes/{pass_id}', { params: { pass_id: passId } });
      toast.success('ClassPass deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete ClassPass');
    }
  };

  const openEditDialog = (pass) => {
    setSelectedPass(pass);
    setForm({
      name: pass.name,
      description: pass.description || '',
      activity_type: pass.activity_type,
      passes_count: pass.passes_count,
      price: pass.price,
      validity_days: pass.validity_days,
      transferable: pass.transferable || false,
      is_membership: pass.is_membership || false,
      membership_billing_cycle: pass.membership_billing_cycle || 'monthly'
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setForm({
      name: '', description: '', activity_type: '', passes_count: 10,
      price: 0, validity_days: 30, transferable: false,
      is_membership: false, membership_billing_cycle: 'monthly'
    });
    setIsEditing(false);
    setSelectedPass(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="studio-classpasses-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">ClassPasses</h1>
            <p className="text-muted-foreground">Manage your ClassPass offerings for clients</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-classpass-btn">
            <Plus className="w-4 h-4 mr-2" />
            New ClassPass
          </Button>
        </div>

        {/* ClassPasses Grid */}
        {classPasses.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classPasses.map((pass, i) => (
              <Card key={pass.id} data-testid={`classpass-card-${i}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{formatActivity(pass.activity_type)}</Badge>
                        {pass.is_membership && (
                          <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Membership
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg">{pass.name}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(pass)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(pass.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  {pass.description && (
                    <CardDescription className="mt-2">{pass.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-muted-foreground" />
                      <span>{pass.passes_count} classes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold">€{pass.price}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>
                        {(pass.validity_days === 0 || pass.validity_days >= 1825)
                          ? 'Never expires'
                          : `${pass.validity_days} days valid`}
                      </span>
                    </div>
                    {pass.transferable && (
                      <Badge variant="secondary" className="text-xs">Transferable</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Ticket className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground text-lg mb-4">No ClassPasses yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create ClassPasses that clients can purchase to book your classes
            </p>
            <Button onClick={() => setShowDialog(true)}>Create Your First ClassPass</Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit ClassPass' : 'Create ClassPass'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the details of this ClassPass' : 'Create a new ClassPass for your clients'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Show alert if no activity types defined */}
            {activityTypes.length === 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Activity Types Defined</AlertTitle>
                <AlertDescription>
                  You need to create activity types before adding ClassPasses.{' '}
                  <Link to="/studio/settings" className="font-medium underline underline-offset-4 hover:text-primary">
                    Go to Settings → Activity Types
                  </Link>{' '}
                  to define your activity types first.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="e.g., 10-Class Yoga Pack"
                required 
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                value={form.description} 
                onChange={(e) => setForm({ ...form, description: e.target.value })} 
                placeholder="Brief description of this pass"
              />
            </div>

            <div className="space-y-2">
              <Label>Activity Type *</Label>
              <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>
                  {activityTypes.map((type, i) => (
                    <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Clients can only redeem this pass for classes of this activity type</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Classes</Label>
                <Input 
                  type="number" 
                  value={form.passes_count} 
                  onChange={(e) => setForm({ ...form, passes_count: parseInt(e.target.value) })} 
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>Price (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={form.price} 
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} 
                  min={0}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Validity (Days)</Label>
              <Input 
                type="number" 
                value={form.validity_days} 
                onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 0 })} 
                min={0}
                data-testid="validity-days-input"
              />
              <p className="text-xs text-muted-foreground">
                {form.validity_days === 0 
                  ? '✓ Never expires - client can use classes anytime'
                  : `Client has ${form.validity_days} days to use all classes after purchase`
                }
              </p>
              <p className="text-xs text-amber-600">
                Tip: Enter 0 if this pass should never expire
              </p>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label>Transferable</Label>
                <p className="text-xs text-muted-foreground">Allow clients to share unused classes</p>
              </div>
              <Switch 
                checked={form.transferable} 
                onCheckedChange={(checked) => setForm({ ...form, transferable: checked })} 
              />
            </div>

            {/* Membership (Auto-Renew) Toggle */}
            <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  <div>
                    <Label>Studio Membership</Label>
                    <p className="text-xs text-muted-foreground">Auto-renew when classes expire</p>
                  </div>
                </div>
                <Switch 
                  checked={form.is_membership} 
                  onCheckedChange={(checked) => setForm({ ...form, is_membership: checked })} 
                  data-testid="membership-toggle"
                />
              </div>
              
              {form.is_membership && (
                <div className="space-y-2 pt-2 border-t">
                  <Label>Billing Cycle</Label>
                  <Select 
                    value={form.membership_billing_cycle} 
                    onValueChange={(v) => setForm({ ...form, membership_billing_cycle: v })}
                  >
                    <SelectTrigger data-testid="billing-cycle-select">
                      <SelectValue placeholder="Select billing cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-600">
                    Clients will be automatically charged when their classes are used up or expire. 
                    They must have a saved payment method.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !form.activity_type || activityTypes.length === 0}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditing ? 'Save Changes' : 'Create ClassPass'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
